import { useMemo } from "react";
import { TrendingUp, Trophy, Zap, AlertTriangle } from "lucide-react";
import { formatFCFA } from "@/lib/crmHelpers";
import { cn } from "@/lib/utils";

const SIGNAL_LABELS = {
  ready_to_close: "Prêts à closer",
  going_cold: "Refroidissement",
  never_contacted: "Jamais contactés",
  overdue_action: "Actions en retard",
  competitor_mention: "Concurrence évoquée",
  premium_qualified: "Premium qualifié",
};

/**
 * High-density pipeline forecast widget — weighted CA, signal counters,
 * top buckets in one row. Replaces the lukewarm "Pipeline" header.
 */
export default function PipelineForecast({ leads = [], insightsMap = {} }) {
  const stats = useMemo(() => {
    let totalAmount = 0;
    let weighted = 0;
    let won = 0;
    let active = 0;
    const signalsCount = {};

    leads.forEach((l) => {
      const ins = insightsMap[l.id];
      const proba = (ins?.probability ?? l.probabilite ?? 0) / 100;
      const amount = Number(l.montant_prevu || 0);

      if (l.statut === "gagne") {
        won += amount;
      } else if (l.statut === "perdu") {
        // ignored
      } else {
        totalAmount += amount;
        weighted += amount * proba;
        active += 1;
      }

      (ins?.signals || []).forEach((s) => {
        signalsCount[s] = (signalsCount[s] || 0) + 1;
      });
    });

    return {
      totalAmount,
      weighted: Math.round(weighted),
      won,
      active,
      signalsCount,
    };
  }, [leads, insightsMap]);

  const weightingPct = stats.totalAmount > 0
    ? Math.round((stats.weighted / stats.totalAmount) * 100)
    : 0;

  // Top 3 signals to surface
  const topSignals = Object.entries(stats.signalsCount)
    .filter(([k]) => SIGNAL_LABELS[k])
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <div
      data-testid="pipeline-forecast"
      className="grid gap-3 rounded-2xl border border-border/60 bg-card p-4 shadow-soft sm:grid-cols-2 lg:grid-cols-4"
    >
      {/* Weighted forecast (hero metric) */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-accent-navy to-accent-blue p-4 text-white sm:col-span-2 lg:col-span-1">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/70">
          <TrendingUp className="h-3 w-3" />
          CA pondéré
        </div>
        <div className="font-heading mt-1 text-2xl font-bold tabular-nums">
          {formatFCFA(stats.weighted)}
        </div>
        <div className="mt-1 text-[11px] text-white/70">
          sur {formatFCFA(stats.totalAmount)} total · {weightingPct}% de pondération
        </div>
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/20">
          <div
            className="h-full bg-accent-gold transition-all"
            style={{ width: `${Math.min(100, weightingPct)}%` }}
          />
        </div>
      </div>

      {/* Active leads */}
      <Stat
        Icon={Zap}
        label="Leads actifs"
        value={stats.active}
        sub={`${leads.length} au total`}
        accent="amber"
      />

      {/* Won */}
      <Stat
        Icon={Trophy}
        label="CA gagné"
        value={formatFCFA(stats.won)}
        sub={leads.filter(l => l.statut === "gagne").length + " ventes"}
        accent="emerald"
      />

      {/* Top signals */}
      <div className="rounded-xl border border-border/60 bg-muted/30 p-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          <AlertTriangle className="h-3 w-3" />
          Signaux clés
        </div>
        {topSignals.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">Pipeline sans alerte 👍</p>
        ) : (
          <ul className="mt-1.5 space-y-1">
            {topSignals.map(([key, count]) => (
              <li key={key} className="flex items-center justify-between text-xs">
                <span className="truncate text-muted-foreground">
                  {SIGNAL_LABELS[key]}
                </span>
                <span
                  className={cn(
                    "tabular-nums font-bold",
                    key === "ready_to_close" ? "text-emerald-600" :
                    key === "premium_qualified" ? "text-violet-600" :
                    "text-red-600"
                  )}
                >
                  {count}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ Icon, label, value, sub, accent = "navy" }) {
  const tones = {
    navy:    "bg-accent-navy/10 text-accent-navy dark:bg-accent-navy/30 dark:text-blue-300",
    amber:   "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  };
  return (
    <div className="rounded-xl border border-border/60 bg-muted/30 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <span className={cn("flex h-5 w-5 items-center justify-center rounded-md", tones[accent])}>
          <Icon className="h-3 w-3" />
        </span>
        {label}
      </div>
      <div className="font-heading mt-1.5 text-lg font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
